import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Attribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  Optional,
  Output,
  Self,
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import {
  CanUpdateErrorState,
  mixinErrorState,
  CanDisable,
  HasTabIndex,
  mixinDisabled,
  mixinTabIndex,
  _AbstractConstructor,
  _Constructor,
} from '@nimbus-library/utils/material-mixins-types';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';

class MatDndChipListBase {
  public readonly stateChanges: Subject<void> = new Subject<void>();

  public constructor(
    public _elementRef: ElementRef,
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    public _parentForm: NgForm,
    public _parentFormGroup: FormGroupDirective,
    public ngControl: NgControl,
  ) {}
}

const _MatTagListMixinBase: _Constructor<HasTabIndex> &
  _AbstractConstructor<HasTabIndex> &
  _Constructor<CanDisable> &
  _AbstractConstructor<CanDisable> &
  _Constructor<CanUpdateErrorState> &
  _AbstractConstructor<CanUpdateErrorState> &
  typeof MatDndChipListBase = mixinTabIndex(mixinDisabled(mixinErrorState(MatDndChipListBase)));

let nextUniqueId = 0;

@Component({
  standalone: false,
  selector: 'nimbus-mat-tag-list',
  templateUrl: './mat-tag-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.disabled]': 'disabled',
  },
  providers: [{ provide: MatFormFieldControl, useExisting: MatTagListComponent }],
})
export class MatTagListComponent
  extends _MatTagListMixinBase
  implements OnDestroy, ControlValueAccessor, CanDisable, HasTabIndex, MatFormFieldControl<string[]>, CanUpdateErrorState, DoCheck
{
  public get focused(): boolean {
    return this.isFocused;
  }

  @Input()
  public get placeholder(): string {
    return this.innerPlaceholder;
  }

  public set placeholder(value: string) {
    this.innerPlaceholder = value;
    this.stateChanges.next();
  }

  @Input()
  public get required(): boolean {
    return this.isRequired;
  }

  public set required(value: boolean) {
    this.isRequired = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  @Input()
  public get value(): string[] {
    return this.innerValue;
  }

  public set value(value: string[]) {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.propagateChange(value);
    }
  }

  @Input()
  public get id(): string {
    return this._id;
  }

  public set id(value: string) {
    this._id = value || this._uid;
    this.stateChanges.next();
  }

  public constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private _ngZone: NgZone,
    _defaultErrorStateMatcher: ErrorStateMatcher,
    elementRef: ElementRef,
    @Optional() _parentForm: NgForm,
    @Optional() _parentFormGroup: FormGroupDirective,
    @Optional() private _parentFormField: MatFormField,
    @Self() @Optional() public ngControl: NgControl,
    @Attribute('tabindex') tabIndex: string,
  ) {
    super(elementRef, _defaultErrorStateMatcher, _parentForm, _parentFormGroup, ngControl);

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this.tabIndex = parseInt(tabIndex) || 0;
  }

  public get empty(): boolean {
    return !this.value || !this.value.length;
  }

  public get shouldLabelFloat(): boolean {
    return !this.empty || this.isFocused || (!!this.inputText && !!this.inputText.trim());
  }
  @HostBinding('class')
  public class: string = 'nimbus-mat-tag-list-component';

  @HostBinding('class.selectable')
  @Input()
  public selectable: boolean = false;

  @Input()
  public selectedValues: string[];

  @Output()
  public selectedValueChange: EventEmitter<string[]> = new EventEmitter();

  private isFocused: boolean = false;

  private isRequired: boolean = false;

  private innerValue: string[];

  private innerPlaceholder: string;

  @Input()
  public inputText: string;

  private _id: string;

  private _uid = `mat-tag-list-${nextUniqueId++}`;

  @Input()
  public errorStateMatcher: ErrorStateMatcher;

  @Input()
  public removable: boolean = true;

  public icons = ICONS;

  public _ariaDescribedby: string;

  public controlType?: string;
  public autofilled?: boolean;
  public userAriaDescribedBy?: string;

  public disabled: boolean;

  public defaultTabIndex: number;
  public tabIndex: number;

  public propagateChange: any = () => {};

  public propagateTouched: any = () => {};

  public ngDoCheck(): void {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  public removeItem(item: string): void {
    const valueIndex: number = this.value.findIndex((valueItem) => item === valueItem);
    const tempValue = JSON.parse(JSON.stringify(this.value));
    tempValue.splice(valueIndex, 1);
    this.value = tempValue;
  }

  public writeValue(value: any): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
    }
    this.changeDetectorRef.markForCheck();
  }

  public registerOnChange(fn: (value: any) => void): void {
    this.propagateChange = fn;
  }

  public registerOnTouched(fn: () => {}): void {
    this.propagateTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.changeDetectorRef.markForCheck();
    this.stateChanges.next();
  }

  public ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  public setDescribedByIds(ids: string[]): void {
    this._ariaDescribedby = ids.join(' ');
  }

  public onContainerClick(): void {
    this.focus();
  }

  public select(item: string): void {
    const index = this.selectedValues.findIndex((s) => s === item);
    const newSelectedValues = [...(this.selectedValues || [])];
    if (index !== -1) {
      newSelectedValues.splice(index, 1);
      this.selectedValueChange.emit(newSelectedValues);
    } else {
      newSelectedValues.push(item);
      this.selectedValueChange.emit(newSelectedValues);
    }
  }

  public focus(options?: FocusOptions): void {
    this._elementRef.nativeElement.focus(options);
  }

  public updateErrorState(): void {
    super.updateErrorState();
  }
}
